import React, { useEffect, useRef } from "react";
import logo from "../../assets/logo.png";
import "aos/dist/aos.css";
import { RippleButton } from "../../pages/Home/Home";

const Hero = ({ bgVideo: video }) => {
    const videoRef = useRef(null);

    useEffect(() => {
        const fn = () => {
            if (videoRef.current) {
                videoRef.current.style.transform = `scale(${1 + window.scrollY / 5000})`;
            }
        };
        window.addEventListener("scroll", fn, { passive: true });
        return () => window.removeEventListener("scroll", fn);
    }, []);

    return (
        <header
            style={{
                position: "relative",
                minHeight: "100vh",
                overflow: "hidden",
                color: "#fff",
                display: "flex",
                flexDirection: "column",
            }}
        >
            {/* Video */}
            <video
                ref={videoRef}
                autoPlay
                loop
                muted
                playsInline
                style={{
                    position: "absolute",
                    inset: 0,
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    transition: "transform 0.1s linear",
                }}
            >
                <source src={video} type="video/mp4" />
            </video>

            {/* Overlay */}
            <div
                style={{
                    position: "absolute",
                    inset: 0,
                    background:
                        "linear-gradient(160deg, rgba(13,10,6,0.88) 0%, rgba(13,10,6,0.75) 50%, rgba(80,30,0,1) 100%)",
                }}
            />

            {/* Ambient Orbs */}
            {[
                {
                    size: 500,
                    color: "#f97316",
                    top: "-15%",
                    right: "-10%",
                    dur: "12s",
                    opacity: 0.12,
                },
                {
                    size: 400,
                    color: "#3b82f6",
                    bottom: "-10%",
                    left: "-8%",
                    dur: "16s",
                    opacity: 0.1,
                },
                {
                    size: 280,
                    color: "#fb923c",
                    top: "30%",
                    left: "15%",
                    dur: "9s",
                    opacity: 0.07,
                },
            ].map((o, i) => (
                <div
                    key={i}
                    style={{
                        position: "absolute",
                        width: o.size,
                        height: o.size,
                        borderRadius: "50%",
                        background: o.color,
                        top: o.top,
                        bottom: o.bottom,
                        left: o.left,
                        right: o.right,
                        filter: `blur(${o.size / 2.5}px)`,
                        opacity: o.opacity,
                        pointerEvents: "none",
                        animation: `orbPulse ${o.dur} ease-in-out infinite ${i * 2}s`,
                    }}
                />
            ))}

            {/* Grid Pattern */}
            <div
                className="grid-pattern"
                style={{
                    position: "absolute",
                    inset: 0,
                    opacity: 0.4,
                    pointerEvents: "none",
                }}
            />

            {/* Content */}
            <div
                style={{
                    position: "relative",
                    zIndex: 2,
                    flex: 1,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    padding: "7rem 1.5rem 3.5rem",
                    textAlign: "center",
                }}
            >
                {/* Badge */}
                <div
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        padding: "0.4rem 1rem",
                        borderRadius: 99,
                        background: "rgba(249,115,22,0.12)",
                        border: "1px solid rgba(249,115,22,0.3)",
                        marginBottom: "1.5rem",
                        animation: "heroFadeUp 0.6s ease 0.1s both",
                    }}
                >
                    <span
                        style={{
                            width: 7,
                            height: 7,
                            borderRadius: "50%",
                            background: "#f97316",
                            display: "inline-block",
                            animation: "spin 3s linear infinite",
                        }}
                    />
                    <span
                        style={{
                            fontSize: 12,
                            fontWeight: 500,
                            color: "rgba(249,115,22,0.9)",
                            letterSpacing: "0.1em",
                            fontFamily: "Syne, sans-serif",
                        }}
                    >
                        ESPACE FINANCIER SÉCURISÉ
                    </span>
                </div>

                {/* Logo + Title */}
                <div
                    style={{
                        animation: "heroFadeUp 0.7s ease 0.2s both",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        textAlign: "center",
                    }}
                >
                    <img
                        src={logo}
                        alt="Logo"
                        style={{
                            width: 160,
                            height: 140,
                            objectFit: "contain",
                            animation: "pulse 1.5s infinite",
                            marginBottom: "1.2rem",
                        }}
                    />

                    <h1
                        className="syne"
                        style={{
                            fontSize: "clamp(1rem, 7vw, 5.6rem)",
                            fontWeight: "bolder",
                            lineHeight: 2.08,
                            letterSpacing: "-0.02em",
                            marginBottom: "0.85rem",
                        }}
                    >
                        Finance
                    </h1>

                    <p
                        style={{
                            fontSize: "clamp(15px, 2vw, 18px)",
                            color: "rgba(255,255,255,0.55)",
                            maxWidth: 480,
                            lineHeight: 1.65,
                            fontWeight: 300,
                        }}
                    >
                        Bienvenue à votre espace financier — épargnez,
                        empruntez et faites fructifier votre argent.
                    </p>
                </div>

                {/* Divider */}
                <div
                    style={{
                        width: 48,
                        height: 2,
                        background:
                            "linear-gradient(90deg, transparent, #f97316, transparent)",
                        margin: "2rem auto",
                        animation: "heroFadeUp 0.7s ease 0.35s both",
                    }}
                />

                {/* Action Buttons */}
                <div
                    style={{
                        display: "flex",
                        flexWrap: "wrap",
                        justifyContent: "center",
                        gap: "0.75rem",
                        animation: "heroFadeUp 0.7s ease 1s both",
                    }}
                >
                    {[
                        {
                            label: "Demande de prêt",
                            bg: "#f97316",
                            color: "#fff",
                            border: "none",
                        },
                        {
                            label: "Remboursement",
                            bg: "rgba(255,255,255,0.07)",
                            color: "#fff",
                            border: "1px solid rgba(255,255,255,0.25)",
                        },
                        {
                            label: "Dépôt",
                            bg: "#fff",
                            color: "#f97316",
                            border: "none",
                        },
                        {
                            label: "Retirer",
                            bg: "rgba(255,255,255,0.07)",
                            color: "#fff",
                            border: "1px solid rgba(255,255,255,0.25)",
                        },
                    ].map((b) => (
                        <RippleButton
                            key={b.label}
                            style={{
                                padding: "0.7rem 1.4rem",
                                borderRadius: 12,
                                background: b.bg,
                                color: b.color,
                                border: b.border,
                                fontSize: 14,
                                fontWeight: 500,
                                fontFamily: "DM Sans, sans-serif",
                                backdropFilter: "blur(10px)",
                                transition: "all 0.25s ease",
                            }}
                            onMouseEnter={(e) =>
                                (e.currentTarget.style.transform =
                                    "translateY(-2px)")
                            }
                            onMouseLeave={(e) =>
                                (e.currentTarget.style.transform =
                                    "translateY(0)")
                            }
                        >
                            {b.label}
                        </RippleButton>
                    ))}
                </div>

                {/* Scroll Hint */}
                <div
                    style={{
                        marginTop: "3rem",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: "0.4rem",
                        opacity: 0.35,
                        animation: "heroFadeUp 0.7s ease 1.2s both",
                    }}
                >
                    <span
                        style={{
                            fontSize: 10,
                            letterSpacing: "0.2em",
                            fontFamily: "Syne, sans-serif",
                        }}
                    >
                        DÉFILER
                    </span>
                    <div
                        style={{
                            width: 1,
                            height: 36,
                            background: "rgba(255,255,255,0.4)",
                            animation: "floatY 2s ease-in-out infinite",
                        }}
                    />
                </div>
            </div>
        </header>
    );
};

export default Hero;